/* eslint-disable no-magic-numbers */
/* eslint-disable complexity */
import React, { memo, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { List, ListRowProps, WindowScroller } from 'react-virtualized';
import clsx from 'clsx';
import Tooltip from 'carbon-react/lib/components/tooltip';
import Icon from 'carbon-react/lib/components/icon';

import { Data, TableFilters, TABLE_COLUMN } from '../../utils/table-filters/filter-table-types';
import { UIFeatherIcon } from '@futrli/ui-components';
import { isFilterByColumn } from '../../utils/table-filters/filter-table-helper';
import * as styles from './virtualized-list.scss';
import {
  EXCLUDE_INCLUDE_DISABLED_KEY,
  InvoicesAndBillsTableRow
} from './invoices-and-bills/invoices-and-bills-table-row';
import { CustomersAndSuppliersTableRow } from './customers-and-suppliers/customers-and-suppliers-table-row';
import { UserGridSettingsContext } from '../../forecast/detail/reports/user-grid-settings/user-grid-settings-context';
import { ExportedPdfsTableRow } from './document-library/exported-pdfs-table-row';
import { ScheduleReportsTableRow } from './document-library/scheduled-reports-table-row';
import { SentPdfTableRow } from './document-library/sent-pdt-table-row';
import { AwaitingReviewTableRow } from './document-library/awaiting-review-table-row';
import exludeArrow from './exclude-arrow.png';
import { useAnimationLatency, useOnboardingUrlInfo } from '../../forecast/onboarding/utils/hooks';
import { SCENARIO_WITH_INVOICES_ONBOARDING_STEP } from '../../forecast/onboarding/const';
import classNames from 'classnames';
import { ContentLoader } from '../content-loader/content-loader';

interface VirtualizedListColumn {
  display_name: ReactNode;
  name: string;
  classes?: string;
  iconName?: string;
  tooltipTitle?: ReactNode;
  hideHeadCell?: boolean;
  hideFilters?: boolean;
  showArrowColumn?: boolean;
}

export enum VIRTUALIZED_LIST_TYPE {
  CUSTOMERS_SUPPLIERS = 'customers-suppliers',
  INVOICES_BILLS = 'invoices-bills',
  EXPORTED_PDFS = 'exported-pdfs',
  SCHEDULED_REPORTS = 'scheduled-reports',
  SENT_PDFS = 'sent-pdfs',
  AWAITING_REVIEW = 'awaiting-review'
}

interface Props {
  data?: Data[];
  handleTableFilterClick: (event: React.MouseEvent<HTMLElement, MouseEvent>, column: TABLE_COLUMN) => void;
  type: VIRTUALIZED_LIST_TYPE;
  filters: TableFilters;
  sortByColumn?: TABLE_COLUMN;
  columns: VirtualizedListColumn[];
  rowHeight?: number;
  countEmptyTable?: number;
  activeRowId?: string;
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  updateRow?: (...args: any[]) => Promise<void>;
  onRowClick?: (id: string, type?: string) => void;
  revalidateData?: () => Promise<void>;
  readOnly?: boolean;
  doubleHeight?: boolean; // allow double the height giving by the window for rending rows
}
interface TableColumnProps {
  column: VirtualizedListColumn;
  filters: TableFilters;
  sortByColumn?: TABLE_COLUMN;
  handleTableFilterClick: (event: React.MouseEvent<HTMLElement, MouseEvent>, column: TABLE_COLUMN) => void;
}

const ROW_HEIGHT = 40;
const COUNT_EMPTY_TABLE = 3;

const TableColumn = ({ column, filters, sortByColumn, handleTableFilterClick }: TableColumnProps) => {
  const { onboardingStep, isOnboardingScenarioWithInvoicesRoute, editInvoicesBills } = useOnboardingUrlInfo();

  const showAnimatedArrows = useAnimationLatency(
    column.showArrowColumn &&
      onboardingStep === SCENARIO_WITH_INVOICES_ONBOARDING_STEP.INVOICES_BILLS_INCLUDED &&
      editInvoicesBills === 'true' &&
      isOnboardingScenarioWithInvoicesRoute
  );
  return (
    <div className={clsx(styles.headerLabel, styles.filterIconContainer, column.classes)} data-testid={column.name}>
      {column.hideHeadCell ? null : (
        <div
          className={clsx(styles.flexRow, styles.filterIconContainerCarbon, {
            [styles.filterByCarbon]: isFilterByColumn(filters, column.name as TABLE_COLUMN, sortByColumn)
          })}
          onClick={e => !column.hideFilters && handleTableFilterClick(e, column.name as TABLE_COLUMN)}
          style={column.showArrowColumn ? { position: 'relative' } : {}}
        >
          {column.hideFilters ? null : (
            <Icon type="filter" fontSize="small" color="--colorsSemanticNeutralYin030" mr="8px" />
          )}
          {column.display_name}
          <img
            alt="onboarding-arrow"
            src={exludeArrow}
            style={{ position: 'absolute', top: -78, left: '50%' }}
            className={classNames(styles.fadeInContainer, {
              [styles.fadeIn]: showAnimatedArrows
            })}
          />
          <img
            alt="onboarding-arrow"
            src={exludeArrow}
            style={{ position: 'absolute', top: -78, left: '70%' }}
            className={classNames(styles.fadeInContainer, {
              [styles.fadeIn]: showAnimatedArrows
            })}
          />
          {column.iconName && column.tooltipTitle && (
            <Tooltip message={column.tooltipTitle} position="top" size="large">
              <div style={{ alignItems: 'center', display: 'flex', marginLeft: 'auto' }}>
                <UIFeatherIcon iconName={column.iconName} />
              </div>
            </Tooltip>
          )}
        </div>
      )}
    </div>
  );
};

export const VirtualizedList = memo(
  ({
    data,
    handleTableFilterClick,
    type,
    filters,
    sortByColumn,
    updateRow,
    columns,
    activeRowId,
    rowHeight = ROW_HEIGHT,
    countEmptyTable = COUNT_EMPTY_TABLE,
    onRowClick,
    revalidateData,
    readOnly = false,
    doubleHeight
  }: Props) => {
    const [disableRowActions, setDisableRowActions] = useState(false);
    const [listWidth, setListWidth] = useState(0);
    const registerChildRef = useRef<HTMLDivElement | null>(null);

    const {
      bulkUpdatesInvoicesBills: {
        handleChangeSelectedInvoicesBills,
        showInvoicesBillsCheckboxes,
        isCustomSelect,
        handleFiltersChange
      }
    } = useContext(UserGridSettingsContext);

    useEffect(() => {
      if (type === VIRTUALIZED_LIST_TYPE.INVOICES_BILLS && showInvoicesBillsCheckboxes) {
        if (!isCustomSelect) {
          handleChangeSelectedInvoicesBills(data ? data?.map(d => d.id) : []);
        }
        handleFiltersChange(filters);
      }
    }, [
      type,
      data,
      handleChangeSelectedInvoicesBills,
      showInvoicesBillsCheckboxes,
      isCustomSelect,
      handleFiltersChange,
      filters
    ]);

    useEffect(() => {
      const element = registerChildRef.current;

      if (!element) {
        return;
      }

      const updateWidth = () => setListWidth(element.clientWidth || 0);
      updateWidth();

      if (typeof ResizeObserver !== 'undefined') {
        const observer = new ResizeObserver(() => updateWidth());
        observer.observe(element);
        return () => observer.disconnect();
      }

      window.addEventListener('resize', updateWidth);

      return () => window.removeEventListener('resize', updateWidth);
    }, [data?.length]);

    const checkDisabledRowActions = () => {
      setDisableRowActions(localStorage.getItem(EXCLUDE_INCLUDE_DISABLED_KEY) === 'true');
    };

    const rowRenderer = ({ key, index, isScrolling, isVisible, style }: ListRowProps) => {
      const row = data ? data[index] : undefined;

      // row content is complex, for better UX a light-weight placeholder while scrolling is rendered.
      const content =
        isScrolling && !isVisible ? (
          <ContentLoader />
        ) : type === VIRTUALIZED_LIST_TYPE.INVOICES_BILLS ? (
          <InvoicesAndBillsTableRow
            rowData={row}
            updateRow={updateRow}
            revalidateData={revalidateData}
            disabledExcludeInclude={disableRowActions}
            checkDisabledRowActions={checkDisabledRowActions}
            readOnly={readOnly}
          />
        ) : type === VIRTUALIZED_LIST_TYPE.CUSTOMERS_SUPPLIERS ? (
          <CustomersAndSuppliersTableRow rowData={row} onRowClick={onRowClick!} activeRowId={activeRowId} />
        ) : type === VIRTUALIZED_LIST_TYPE.EXPORTED_PDFS ? (
          <ExportedPdfsTableRow rowData={row} rowIndex={index} revalidateData={revalidateData} />
        ) : type === VIRTUALIZED_LIST_TYPE.SCHEDULED_REPORTS ? (
          <ScheduleReportsTableRow rowData={row} rowIndex={index} revalidateData={revalidateData} />
        ) : type === VIRTUALIZED_LIST_TYPE.SENT_PDFS ? (
          <SentPdfTableRow rowData={row} rowIndex={index} />
        ) : type === VIRTUALIZED_LIST_TYPE.AWAITING_REVIEW ? (
          <AwaitingReviewTableRow rowData={row} rowIndex={index} revalidateData={revalidateData} />
        ) : null;

      return (
        <div
          key={key}
          style={style}
          className={clsx({
            [styles.clickable]: !!onRowClick && row?.id !== activeRowId
          })}
        >
          {content}
        </div>
      );
    };

    const rowHeaderRenderer = () => {
      return (
        <header className={clsx(styles.virtualizedListHead)}>
          {columns.map((column, index) => (
            <TableColumn
              key={`virtualized-list-header-${index}`}
              column={column}
              handleTableFilterClick={handleTableFilterClick}
              sortByColumn={sortByColumn}
              filters={filters}
            />
          ))}
        </header>
      );
    };

    if (!data) {
      return null;
    }

    return (
      <div>
        <section className={clsx(styles.tableContainer, styles.hasStickyCashFlow)} data-testid="virtualized-list">
          <WindowScroller>
            {({ height, isScrolling, onChildScroll, registerChild, scrollTop }) => {
              return (
                <div className={styles.windowScrollerWrapper}>
                  {rowHeaderRenderer()}

                  {!data.length && (
                    <div data-testid="empty-virtualized-list-table" className={styles.emptyVirtualizedList}>
                      {Array(countEmptyTable)
                        .fill(null)
                        .map((_el, index) => (
                          <div key={index} style={{ height: 40, display: 'flex', alignItems: 'center' }}>
                            <ContentLoader animation={false} />
                          </div>
                        ))}
                    </div>
                  )}
                  <div
                    data-testid="virtualized-list-register-child"
                    ref={element => {
                      registerChildRef.current = element;
                      (registerChild as ((el: Element | null) => void) | undefined)?.(element);
                    }}
                    style={{ overflow: 'initial' }}
                  >
                    <List
                      className={styles.virtualizedList}
                      autoHeight
                      height={(doubleHeight ? 2 : 1) * height}
                      isScrolling={isScrolling}
                      rowCount={data.length}
                      rowHeight={rowHeight}
                      rowRenderer={rowRenderer}
                      onScroll={onChildScroll}
                      scrollTop={scrollTop}
                      width={listWidth || 1}
                      overscanRowCount={5}
                    />
                  </div>
                </div>
              );
            }}
          </WindowScroller>
        </section>
      </div>
    );
  }
);
